import { navigate } from 'gatsby';
import React from 'react';
import Helmet from 'react-helmet';
import { injectIntl } from 'react-intl';
import { Subscribe } from 'unstated';
import { Router } from '@reach/router';

import AuthenticatedRoute from '../components/AuthenticatedRoute';
import paths from '../paths';
import {
  ReservationContainer,
  ReservationProvider,
} from '../utils/Reservation';
import Bundle from './_bundle';
import Cart from './_cart';
import Checkout from './_checkout';
import Configure from './_configure';
import Confirmation from './_confirmation';
import CreateAccountPage from './_create-account';
import InvitationExpiredPage from './_invitation-expired';
import LoginPage from './_login';
import NoReservationPage from './_no-reservation';
import TermsAndConditions from './_terms-and-conditions';

const propTypes = {};

const useLocalData = pathname =>
  pathname === paths.bundle ||
  pathname === paths.configure ||
  pathname === paths.cart;

const FakeRedirect = ({ path }) => {
  navigate(path);
  return null;
};

class ReservationPage extends React.Component {
  constructor(...args) {
    super(...args);

    const { location } = this.props;
    this.checkLocation(location);
  }

  componentDidUpdate() {
    this.checkLocation(this.props.location);
  }

  checkLocation = ({ pathname }) => {
    if (useLocalData(pathname)) {
      this.props.cart.clearCart();
    }
  };

  render() {
    const { location, cart } = this.props;

    const { remote } = cart;

    if (!remote) {
      navigate(paths.noReservation);
      return null;
    }
    if (
      // Once a payment is made redirect everything to the confirmation screen
      remote.payment &&
      location.pathname !== paths.confirmation
    ) {
      navigate(paths.confirmation);
      return null;
    }
    let initialPage = remote.isDeviceOnly ? paths.cart : paths.bundle;

    if (location.pathname === '/') {
      navigate(initialPage);
      return null;
    }

    if (cart.plan) initialPage = paths.configure;
    if (remote.cart) initialPage = paths.payment;

    return (
      <Router>
        {React.Children.map(
          [
            <AuthenticatedRoute path={paths.bundle} component={Bundle} />,
            cart.plan && (
              <AuthenticatedRoute
                path={paths.configure}
                component={Configure}
              />
            ),
            !!cart.numDevices && (
              <AuthenticatedRoute path={paths.cart} component={Cart} />
            ),
            !!cart.numDevices && (
              <AuthenticatedRoute
                path={`${paths.checkout}*`}
                component={Checkout}
              />
            ),
            remote.payment && (
              <AuthenticatedRoute
                path={paths.confirmation}
                component={Confirmation}
              />
            ),
            location.pathname !== initialPage && (
              <FakeRedirect path={initialPage} />
            ),
          ].filter(Boolean),
          c => c,
        )}
      </Router>
    );
  }
}

ReservationPage.propTypes = propTypes;

export default injectIntl(({ intl, ...props }) => {
  const router = (
    <Router>
      <LoginPage path="/login/" />
      <CreateAccountPage path="/create-account/" />
      <NoReservationPage path="/no-reservation/" />
      <TermsAndConditions path={paths.termsAndConditions} />
      <InvitationExpiredPage path="/invitation-expired/" />
      <AuthenticatedRoute
        path="*"
        render={() => (
          <ReservationProvider>
            <Subscribe to={[ReservationContainer]}>
              {cart => <ReservationPage cart={cart} {...props} />}
            </Subscribe>
          </ReservationProvider>
        )}
      />
    </Router>
  );

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: 'title',
            defaultMessage: 'Butterfly Store',
          })}
        </title>
      </Helmet>

      {router}
    </>
  );
});
